import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMachines, selectMachineById } from '../../features/machines/machinesSlice';
import { useUpdateMutation } from '../../app/services/machines';
import ModalComponent from '../ModalComponent';
import { useTranslation } from 'react-i18next';
import { iconButtonStyles, iconButtonStylesLarge } from '../../utils/iconButtonStyles';

import { Typography, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';

function ModalStatusMachine({ id, action, onSuccess, size = 'small' }) {
  const [open, setOpen] = useState(false);
  const [errorResponse, setErrorResponse] = useState('');

  const selectedMachine = useSelector((state) => selectMachineById(state, id));

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updateMachine] = useUpdateMutation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!selectedMachine) {
      return;
    }

    try {
      const { id, ...machineData } = selectedMachine;
      machineData.isArchived = action;

      await updateMachine({ id, machineData });

      dispatch(fetchMachines());
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      setErrorResponse(error);
      console.error('Error updating machine:', error);
    }
  };

  return (
    <div>
      <Tooltip
        title={action === 'archived' ? t('modals.archive') : t('modals.reactivate')}
        placement="top"
      >
        <IconButton
          color="primary"
          onClick={handleOpen}
          style={size === 'large' ? iconButtonStylesLarge : iconButtonStyles}
        >
          {action === 'archived' ? <ArchiveIcon /> : <UnarchiveIcon />}
        </IconButton>
      </Tooltip>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {t('modals.areSureWant')}{' '}
            {action === 'archived' ? t('modals.archive') : t('modals.reactivate')}{' '}
            {t('modals.thisMachine')}?
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers sx={{ pr: 2, pl: 2 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              {selectedMachine && (
                <Typography
                  variant="h6"
                  style={{ color: 'var(--text-heading)', fontWeight: 'bold' }}
                >
                  {selectedMachine?.name}
                </Typography>
              )}
            </div>
            <Typography color="var(--text-secondary)" variant="body2" pt={1}>
              {action === 'archived'
                ? t('generic.archiveMachineDescription')
                : t('generic.restoreMachineDescription')}
            </Typography>
          </DialogContent>

          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
              {action === 'archived' ? t('modals.archive') : t('modals.reactivate')}
            </ModalButton>
          </ModalDialogActions>

          {errorResponse && (
            <DialogContent sx={{ p: 3, pt: 0 }}>
              {Array.isArray(errorResponse) ? (
                errorResponse.map((item, index) => (
                  <Typography key={index} color="error">
                    {item}
                  </Typography>
                ))
              ) : (
                <Typography color="error">{errorResponse}</Typography>
              )}
            </DialogContent>
          )}
        </div>
      </ModalComponent>
    </div>
  );
}

export default ModalStatusMachine;
